import React, { useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetArticle from "./getArticle.jsx";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& .page-content-inner": {
      marginTop: 0,
      marginBottom: 0,
    },
    "& .tabbedPinnedGrid": {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
    "& .tabbedPanelGrid": {
      marginTop: theme.spacing(1),
      marginBottom: 30,
    },
  },
  tabBar: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    background: "var(--color-background-default)",
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: 20,
    paddingRight: 20,
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 12,
      paddingRight: 12,
    },
  },
  tabBarFirst: {
    marginTop: theme.spacing(-3.5),
  },
  tabsContainer: {
    width: "100%",
    minHeight: "unset",
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-flexContainer": {
      gap: 6,
    },
    "& .MuiTabs-scrollButtons": {
      width: 22,
    },
    [theme.breakpoints.down("sm")]: {
      "& .MuiTabs-flexContainer": {
        gap: 4,
      },
    },
  },
  pillTab: {
    textTransform: "none",
    fontSize: "0.8125rem",
    fontWeight: 600,
    minWidth: "unset",
    minHeight: 30,
    marginBottom: theme.spacing(0.5),
    padding: "4px 16px",
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(8px * var(--app-radius-scale, 1))",
    color: "var(--color-text-primary)",
    backgroundColor: "var(--color-background-paper)",
    opacity: 1,
    transition:
      "color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease",
    "&.Mui-selected": {
      color: "var(--color-primary-text)",
      borderColor: "var(--color-primary-main)",
      backgroundColor: "var(--color-background-paper)",
      fontWeight: 700,
      boxShadow:
        "inset 0 0 0 1px var(--color-primary-main), 0 1px 4px rgba(0, 0, 0, 0.08)",
    },
    "&:hover:not(.Mui-selected)": {
      borderColor: "var(--color-primary-main)",
      backgroundColor: "var(--total_row_background)",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      minHeight: 28,
      padding: "3px 12px",
    },
  },
}));

const TabbedCards = ({
  tabs,
  device,
  isReadOnly,
  parentThis,
  templateSecurity,
}) => {
  const classes = useStyles();

  const allGroups = tabs || [];
  const pinnedCards = allGroups
    .filter((group) => group.pinned)
    .reduce((acc, group) => acc.concat(group.cards), []);
  const tabGroups = allGroups.filter((group) => !group.pinned);

  const storageKey = `tabbedCards:activeTab:${
    typeof window !== "undefined" ? window.location.pathname : ""
  }:${tabGroups.map((group) => group.label).join("|")}`;

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = parseInt(sessionStorage.getItem(storageKey), 10);
      return Number.isNaN(stored) || stored < 0 ? 0 : stored;
    } catch {
      return 0;
    }
  });

  const safeIndex = activeTab < tabGroups.length ? activeTab : 0;
  const activeGroup = tabGroups[safeIndex];

  const handleChange = (event, newValue) => {
    localStorage.removeItem("chartStorageKeys");
    setActiveTab(newValue);
    sessionStorage.setItem(storageKey, String(newValue));
  };

  const renderCards = (cards, keyPrefix, extraClass) => (
    <article className={`page-content-inner ${extraClass || ""}`.trim()}>
      {cards.map((card, index) => (
        <GetArticle
          key={`${keyPrefix}-${index}`}
          item={card}
          device={device}
          index={index}
          isReadOnly={isReadOnly}
          parentThis={parentThis}
          templateSecurity={templateSecurity}
        />
      ))}
    </article>
  );

  if (!allGroups.length) return null;

  return (
    <div className={classes.root}>
      {pinnedCards.length > 0 &&
        renderCards(pinnedCards, "pinned", "tabbedPinnedGrid")}

      {tabGroups.length > 0 && (
        <>
          <div
            className={`${classes.tabBar} ${
              pinnedCards.length === 0 ? classes.tabBarFirst : ""
            }`}
          >
            <Tabs
              variant="fullWidth"
              value={safeIndex}
              onChange={handleChange}
              className={classes.tabsContainer}
              aria-label="card layout tabs"
            >
              {tabGroups.map((group, index) => (
                <Tab
                  key={index}
                  disableRipple
                  className={classes.pillTab}
                  label={group.label}
                  id={`card-tab-${index}`}
                  aria-controls={`card-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </div>
          <div
            role="tabpanel"
            id={`card-tabpanel-${safeIndex}`}
            aria-labelledby={`card-tab-${safeIndex}`}
          >
            {activeGroup &&
              renderCards(
                activeGroup.cards,
                `tab-${safeIndex}`,
                "tabbedPanelGrid"
              )}
          </div>
        </>
      )}
    </div>
  );
};

TabbedCards.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      cards: PropTypes.array,
    })
  ),
  device: PropTypes.string,
  isReadOnly: PropTypes.bool,
  parentThis: PropTypes.object.isRequired,
  templateSecurity: PropTypes.object,
};

export default TabbedCards;
